// src/lib/corrections.js
//
// #682 and #702, Session 79.22b.6c.2 -- PUTTING A RECORDED NUMBER RIGHT AFTER
// ITS UNDO HAS GONE. The corrections gate:
//   docs/gates/Stede_Corrections_Gate_682_702_761.html
//
// Owen signs off 100 frames and made 40; he loads 40 on a truck that carried 4;
// Dallas counts in 4 when 40 came off. The person who recorded it, or a branch
// lead there, opens the transfer, presses Correct on the line, types the right
// number, picks a reason and saves (682 D1, D3, D6). The database does the work
// in one step each -- correct_sign_off(), correct_load(), correct_count_in() --
// and every one of them refuses in a sentence a person can act on. This file is
// the screens' one way in: the three calls, the checks made on Save before a
// call is spent, and every sentence the correction sheet and its answer say.
//
// THE SENTENCES ARE PURE FUNCTIONS, NOT JSX, for the reason standingSentence()
// gives in transferLoads.js: the words are the deliverable and the gate's copy
// table (section 7, F2) fixes them to the word, so a test holds them.
//
// THE ANSWER IS SHOWN ONLY AFTER THE DATABASE PROVES IT LANDED (gate B3): a
// correction id back from the step, and then the transfer read again by the
// caller. `error === null` is never proof on its own.

import { supabase } from './supabase';
import { receivedAverageCost, plainTransferRefusal, askingApproverIds, isOwnShelf } from './branchTransfers';
import { sendNotification, getRequestApproverIds } from './notify';
import { orEveryoneWhoCanDecide } from './decisionMakers';
import { canCorrectStep, canAnswerTruckExtra } from './permissions';

// 682 D4 -- ONE TAP FROM A SHORT LIST. Wrong number is chosen when the sheet
// opens (682 D17). `counted_more` is written only by Houston's Yes and is never
// offered here.
export const CORRECTION_REASONS = Object.freeze([
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'recorded_twice', label: 'Recorded twice' },
  { value: 'didnt_happen', label: "Didn't happen" },
  { value: 'other', label: 'Other' },
]);
const REASON_LABEL = Object.freeze(Object.fromEntries(CORRECTION_REASONS.map(r => [r.value, r.label])));

// HOW A REASON READS in the history and the bell (F2): the reason, and a note
// given with it follows in quotes; Other reads as the note itself, in quotes.
export function reasonText(reason, note = null) {
  const words = typeof note === 'string' && note.trim() ? `“${note.trim()}”` : null;
  if (reason === 'other') return words || 'Other';
  const label = REASON_LABEL[reason] || null;
  if (!label) return words;
  return words ? `${label} ${words}` : label;
}

// Numbers print as a person writes them: 40, 2.5 -- never 40.00.
export function qtyText(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return String(Math.round(v * 100) / 100);
}

// #761 / gate B6, Session 79.22b.6c.3 -- A TAKE-BACK A CORRECTION WROTE READS
// "CORRECTED", NEVER "UNDONE", wherever stock records are listed: the item's
// page, the Ledger and a record's detail panel. Its fresh record keeps its own
// type's word (Made, Transfer in, Transfer out). `correction_id` must be read.
export const CORRECTED_LABEL = 'Corrected';
export function isCorrectionTakeBack(tx) {
  return tx?.tx_type === 'reversal' && tx?.correction_id != null;
}

// THE NUMBER BOX (F3): digits, and at most two decimals, the database's own
// limit. Null for an empty box, NaN for anything else that is not a number.
export function parseTyped(typed) {
  const s = String(typed == null ? '' : typed).trim();
  if (s === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
  return Number(s);
}

// What the box will take as it is typed: digits and one point, two decimals.
export function cleanTyped(typed) {
  const s = String(typed == null ? '' : typed).replace(/[^\d.]/g, '');
  const [whole, ...rest] = s.split('.');
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
}

export const CORRECTION_WORDS = Object.freeze({
  empty: 'Type the right number.',
  same: "That's the number already recorded — type the right one.",
  other: 'Say what happened — a few words are needed for Other.',
  failed: "Couldn't save the correction — nothing was changed. Try again.",
});

// CHECKED ON SAVE, NEVER WHILE TYPING (F3). `field` says where the sentence
// sits: under the number box (which takes a red outline) or under the note.
export function checkCorrection({ typed, current, reason, note }) {
  const qty = parseTyped(typed);
  if (qty == null || Number.isNaN(qty) || qty < 0) return { field: 'number', text: CORRECTION_WORDS.empty };
  if (Number(current) === qty) return { field: 'number', text: CORRECTION_WORDS.same };
  if (reason === 'other' && !(typeof note === 'string' && note.trim())) {
    return { field: 'note', text: CORRECTION_WORDS.other };
  }
  return { qty };
}

// A REFUSAL ABOUT THE NUMBER sits under the box, like S1; every other refusal
// sits where S4's sentence does, above the buttons.
const NUMBER_REFUSALS = [
  / — correct to [\d.]+ (or more|or fewer)/,
  /^That's the number already recorded/,
  /^Type the right number\./,
];
export function refusalField(text) {
  return NUMBER_REFUSALS.some(re => re.test(String(text || ''))) ? 'number' : 'other';
}

// ── WHAT WILL HAPPEN, said before the press (callout E). ─────────────────────
// Save is the confirmation; there is no "are you sure?" (gate 665 D22).

// A sign-off: the making branch's shelf, and the transfer's made count.
export function willHappenSignOff({ branch, was, now, lineMade, asked }) {
  const d = Math.abs(Number(now) - Number(was));
  if (!(d > 0)) return null;
  const madeAfter = (Number(lineMade) || 0) - Number(was) + Number(now);
  const shelf = Number(now) < Number(was)
    ? `${branch}'s shelf goes down ${qtyText(d)}.`
    : `${branch}'s shelf goes up ${qtyText(d)}.`;
  return `${shelf} The transfer will read ${qtyText(madeAfter)} of ${qtyText(asked)} made.`;
}

// A truck, at the branch it left: that shelf, and what the other branch hears.
export function willHappenLoad({ branch, dest, was, now }) {
  const d = Math.abs(Number(now) - Number(was));
  if (!(d > 0)) return null;
  const shelf = Number(now) < Number(was)
    ? `${branch}'s shelf gets ${qtyText(d)} back.`
    : `${branch}'s shelf goes down ${qtyText(d)} more.`;
  return `${shelf} ${dest} will be told the truck carried ${qtyText(now)}.`;
}

// A count, at the branch that counted it. Above what the truck carried, the
// truck's number is booked and the sending branch is asked about the rest
// (682 D7) -- only on a truck its sender can put right; on any other the
// database refuses on Save, so nothing is promised here.
export function willHappenCount({ branch, sender, was, now, loaded, askable = true }) {
  const n = Number(now);
  const carried = Number(loaded);
  if (Number.isFinite(carried) && n > carried) {
    if (!askable) return null;
    const extra = n - carried;
    return `${qtyText(n)} counted — ${qtyText(extra)} more than ${sender}'s load sheet. `
      + `Saving books in the ${qtyText(carried)} it carried and asks ${sender} about the other ${qtyText(extra)}.`;
  }
  const d = Math.abs(n - Number(was));
  if (!(d > 0)) return null;
  return n < Number(was)
    ? `${branch}'s shelf goes down ${qtyText(d)}.`
    : `${branch}'s shelf goes up ${qtyText(d)}.`;
}

// ── THE ANSWER AFTER SAVING (callout F), until the next press. ───────────────
// Each is [the bold line, the line beneath it or null].

export function answerSignOff({ now, item, made, asked, ready, sent }) {
  const readyNow = Math.max((Number(ready) || 0) - (Number(sent) || 0), 0);
  return [
    `Corrected to ${qtyText(now)} ${item} · ${qtyText(made)} of ${qtyText(asked)} made`,
    `To load now shows ${qtyText(readyNow)} ready.`,
  ];
}

export function answerLoad({ loadRef, now, item, dest }) {
  return [`Corrected ${loadRef} to ${qtyText(now)} ${item} · ${dest} has been told.`, null];
}

// A count corrected above its truck books the truck's number and asks the
// sender; the second line is Count in's own after-sentence (P5), so the two
// screens say it alike.
export function answerCount({ now, item, loadRef, asked = [], sender }) {
  const extra = (Array.isArray(asked) ? asked : []).reduce((n, a) => n + (Number(a?.extra) || 0), 0);
  return [
    `Corrected to ${qtyText(now)} ${item} counted in from ${loadRef}.`,
    extra > 0 ? `${sender} has been asked to confirm the other ${qtyText(extra)}.` : null,
  ];
}

// #761, T5, Session 79.22b.6c.3 -- A RUN FOR THE SHELF, from the item's Made
// line (682 D6, callout R). The shelf moves by the difference; a shelf that would
// go below zero is WARNED, never blocked, because a count may go negative and
// correct_record_production() never refuses for it (F2, X2).
export function willHappenShelfRun({ branch, was, now, onHand = null }) {
  const d = Math.abs(Number(now) - Number(was));
  if (!(d > 0)) return null;
  const line = Number(now) < Number(was)
    ? `${branch}'s shelf goes down ${qtyText(d)}.`
    : `${branch}'s shelf goes up ${qtyText(d)}.`;
  const left = onHand == null ? null : Number(onHand) - Number(was) + Number(now);
  return left != null && Number.isFinite(left) && left < 0
    ? `${line} That leaves ${qtyText(left)} on the shelf — count it if that's wrong.`
    : line;
}

export function answerShelfRunCorrection({ now, item, branch, onHand }) {
  return [`Corrected to ${qtyText(now)} ${item} · ${branch}'s rack is now at ${qtyText(onHand)}.`, null];
}

// ── THE AVERAGE COST (#240: the app works it out, the database writes it) ────
//
// A count corrected UP books more at the truck's cost: the receive path's own
// blend, as Count in does (receivedAverageCost). Corrected DOWN, those units
// leave at the truck's cost, which takes them back out of the blend. With no
// cost on the truck, no average yet, or nothing left on the shelf afterwards,
// null -- which the database reads as "leave the average as it was" (X2).
export function averageAfterCountChange({ qtyOnHand, avg, delta, unitCost }) {
  const d = Number(delta);
  if (!d || unitCost == null || !Number.isFinite(Number(unitCost))) return null;
  const q = Number(qtyOnHand) || 0;
  let next;
  if (d > 0) {
    next = receivedAverageCost({ currentQty: q, currentAvg: avg, receivedQty: d, unitCostAtTransfer: unitCost });
  } else {
    if (avg == null || !Number.isFinite(Number(avg))) return null;
    const left = q + d;
    if (!(left > 0)) return null;
    next = (q * Number(avg) + d * Number(unitCost)) / left;
  }
  return next != null && Number.isFinite(Number(next)) && Number(next) >= 0 ? Number(next) : null;
}

// ── THE THREE CALLS ──────────────────────────────────────────────────────────
//
// Each returns { result } once the database names the correction it wrote, or
// { refusal, race } in the database's own words, or { failure } when the save
// never reached it -- S4, "nothing was changed".

function answered(error) {
  const code = String(error?.code || '');
  const message = String(error?.message || '').trim();
  // A step that refused writes a Postgres state code with its sentence. Anything
  // else -- a dropped connection, a missing step -- never reached a decision.
  if (/^[0-9A-Z]{5}$/.test(code) && message) {
    const plain = plainTransferRefusal(message);
    return { refusal: plain.text, race: !!plain.race };
  }
  return { failure: CORRECTION_WORDS.failed };
}

function noteOrNull(note) {
  return typeof note === 'string' && note.trim() ? note.trim() : null;
}

export async function correctSignOff({ productionId, qty, reason, note = null }) {
  const { data, error } = await supabase.rpc('correct_sign_off', {
    p_production_id: productionId, p_qty: qty, p_reason: reason, p_note: noteOrNull(note),
  });
  if (error) return answered(error);
  if (!data || data.correction_id == null) return { failure: CORRECTION_WORDS.failed };
  return { result: data };
}

// p_was is the number the screen showed. A different number in the database
// means somebody corrected it in between, and nothing is written: the race
// sentence comes back instead.
export async function correctLoad({ loadItemId, qty, reason, note = null, was = null }) {
  const { data, error } = await supabase.rpc('correct_load', {
    p_load_item_id: loadItemId, p_qty: qty, p_reason: reason, p_note: noteOrNull(note),
    p_was: was == null ? null : Number(was),
  });
  if (error) return answered(error);
  if (!data || data.correction_id == null) return { failure: CORRECTION_WORDS.failed };
  return { result: data };
}

// The counting branch's shelf is read fresh HERE, immediately before the call,
// as Count in reads it, so the average is never worked out from a number the
// screen loaded an hour ago. A failed read stops the save before anything is
// written. What moves is the BOOKED number: never more than the truck carried,
// because the rest is a question for the sender (682 D7).
export async function correctCountIn({
  loadItemId, qty, reason, note = null, was = null, branchId, itemId, unitCost = null, loaded = null,
}) {
  let avgCost = null;
  const carried = loaded == null ? null : Number(loaded);
  const booked = carried != null && Number(qty) > carried ? carried : Number(qty);
  const delta = booked - (Number(was) || 0);
  if (delta && unitCost != null && branchId != null && itemId != null) {
    const { data: stock, error: stockError } = await supabase
      .from('inventory_stock').select('qty_on_hand, avg_unit_cost')
      .eq('branch_id', branchId).eq('item_id', itemId).maybeSingle();
    if (stockError) return { failure: CORRECTION_WORDS.failed };
    avgCost = averageAfterCountChange({
      qtyOnHand: stock?.qty_on_hand ?? 0, avg: stock?.avg_unit_cost ?? null, delta, unitCost,
    });
  }
  const { data, error } = await supabase.rpc('correct_count_in', {
    p_load_item_id: loadItemId, p_qty: qty, p_reason: reason, p_note: noteOrNull(note),
    p_avg_cost: avgCost, p_was: was == null ? null : Number(was),
  });
  if (error) return answered(error);
  // Only a question opened (counted above a truck already booked in full): no
  // correction record, and the question is the proof.
  const asked = Array.isArray(data?.asked) ? data.asked : [];
  if (!data || (data.correction_id == null && asked.length === 0)) return { failure: CORRECTION_WORDS.failed };
  return { result: data };
}

// #761, T5 -- the run's LATEST live stock record, the item's Made line.
// correct_record_production() (20261005_correct_sign_off_and_shelf_run_702_761.sql,
// statement 13) returns {correction_id, tx_id (the fresh record, null at 0),
// item_id, branch_id, was, now, qty_on_hand}.
export async function correctRecordProduction({ txId, qty, reason, note = null }) {
  const { data, error } = await supabase.rpc('correct_record_production', {
    p_tx_id: txId, p_qty: qty, p_reason: reason, p_note: noteOrNull(note),
  });
  if (error) return answered(error);
  if (!data || data.correction_id == null) return { failure: CORRECTION_WORDS.failed };
  return { result: data };
}

// ── READ BESIDE THE LOADS, for the history ───────────────────────────────────
//
// Read on every tier, never behind a width guard (F4, #551's lesson). A read
// that fails leaves the history drawing the plain lines rather than empty.
const CORRECTION_SELECT = 'id, kind, branch_id, item_id, transfer_id, transfer_number, load_id, load_ref, '
  + 'load_item_id, count_in_id, original_production_id, original_tx_id, was_qty, now_qty, reason, note, '
  + 'step_recorded_by, step_recorded_by_name, corrected_by, corrected_by_name, corrected_at, replaces_id';
export async function fetchCorrections(transferId) {
  if (transferId == null) return { data: [], error: null };
  const { data, error } = await supabase
    .from('stock_corrections').select(CORRECTION_SELECT).eq('transfer_id', transferId)
    .order('id', { ascending: true });
  if (error) return { data: [], error };
  return { data: data || [], error: null };
}

export async function fetchLoadExtras(transferId) {
  if (transferId == null) return { data: [], error: null };
  const { data, error } = await supabase
    .from('transfer_load_extras').select('*').eq('transfer_id', transferId)
    .order('id', { ascending: true });
  if (error) return { data: [], error };
  return { data: data || [], error: null };
}

// ── HOUSTON'S ANSWER: "DID 4 GO ON THE TRUCK?" (682 D7, D13, D16) ────────────
//
// #682, Session 79.22b.6c.2b. Dallas counted more than the truck's sheet; the
// database booked the sheet's number and asked the sending branch about the
// rest (count_in_load(), statement 13 of 20261006_correct_truck_and_count_682.sql).
// The sending branch answers on its Awaiting confirmation tab, in one press:
// Yes corrects the truck and books the extra at Dallas; No and Settle record
// the answer and move nothing (answer_load_extra(), statement 12).

// WHO RECORDED THE TRUCK LINE'S CURRENT NUMBER -- the `loadedBy` that
// canAnswerTruckExtra() is given. answer_load_extra()'s own test, copied: the
// latest truck correction of that line, by id, names its corrector; with none,
// the truck's loader (682 D15). A correction whose corrector has since gone
// names nobody, as the database's does, and then only a branch lead may answer.
export function truckLineRecorder({ corrections = [], loadItemId, loadedBy = null } = {}) {
  const fixes = (Array.isArray(corrections) ? corrections : [])
    .filter(c => c && c.kind === 'load' && String(c.load_item_id) === String(loadItemId));
  if (!fixes.length) return loadedBy ?? null;
  const latest = fixes.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a));
  return latest.corrected_by ?? null;
}

const lineQty = (loads, loadItemId) => {
  for (const l of loads) {
    for (const li of (l?.branch_transfer_load_items || [])) {
      if (String(li?.id) === String(loadItemId)) return Number(li.qty_loaded) || 0;
    }
  }
  return null;
};

// IS THERE ROOM ON THE ORDER FOR THE EXTRA? (682 D16) answer_load_extra()'s own
// test, copied: the transfer still open (approved, or on the road), and the
// other trucks from the sending branch -- undone ones left out -- carrying this
// item, plus the number counted, no more than the line asks for (nothing, once
// the line was refused). With no room the card offers Settle alone, in the
// database's own words, which name `asked` and `shipped` (this truck included).
// Whether enough is signed off as made is NOT asked here: D16 keeps Yes and No
// on that card, and Yes is answered by the database.
export function extraRoom({ question, transfer, loads = [] } = {}) {
  if (!question || !transfer) return { room: false, asked: 0, shipped: 0 };
  const list = (Array.isArray(loads) ? loads : []).filter(l => l
    && (l.transfer_id == null || String(l.transfer_id) === String(question.transfer_id)));
  const line = (transfer.branch_transfer_items || [])
    .find(li => li && String(li.item_id) === String(question.item_id)) || null;
  const asked = !line || line.line_status === 'refused' ? 0 : (Number(line.qty_requested) || 0);
  const others = list
    .filter(l => String(l.from_branch_id) === String(transfer.from_branch_id)
      && l.status !== 'undone' && String(l.id) !== String(question.load_id))
    .reduce((n, l) => n + (l.branch_transfer_load_items || [])
      .filter(li => li && String(li.item_id) === String(question.item_id))
      .reduce((m, li) => m + (Number(li.qty_loaded) || 0), 0), 0);
  const thisTruck = lineQty(list, question.load_item_id) ?? (Number(question.loaded_qty) || 0);
  const open = transfer.status === 'approved' || transfer.status === 'in_transit';
  return {
    room: open && others + (Number(question.counted_qty) || 0) <= asked,
    asked,
    shipped: others + thisTruck,
  };
}

const ANSWER_STATUS = Object.freeze({ went: 'confirmed', did_not_go: 'declined', settle: 'settled_by_count' });

// THE ANSWER, in one press. On Yes the extra lands on the counting branch's
// shelf at the truck's cost, so that branch's new average is worked out HERE
// (#240; the gate's B3: "sets Dallas's average from p_avg_cost, which the app
// works out") from the truck line's cost and the counting branch's shelf, both
// read fresh immediately before the call, as a count's correction reads its
// shelf. A failed read stops the answer before anything is written. No and
// Settle move nothing, so they pass no average.
//
// PROVEN BY THE DATABASE'S OWN ANSWER (gate B3): the question's id back, its
// status the one this answer writes, and on Yes the truck's correction and the
// booked record named. The caller then reads the page again before the card
// shows the answer. A failure is S4's sentence, in the card (F1).
// #682, 79.22b.6c.3 -- and once it has landed, the counter's bell (B5), with the
// names the caller already holds.
export async function answerLoadExtra({ question, answer, branchNames = {}, itemName = null, transferNumber = null } = {}) {
  if (!question || question.id == null || !ANSWER_STATUS[answer]) return { failure: CORRECTION_WORDS.failed };
  let avgCost = null;
  if (answer === 'went') {
    const [{ data: li, error: liError }, { data: stock, error: stockError }] = await Promise.all([
      supabase.from('branch_transfer_load_items').select('unit_cost_at_transfer')
        .eq('id', question.load_item_id).maybeSingle(),
      supabase.from('inventory_stock').select('qty_on_hand, avg_unit_cost')
        .eq('branch_id', question.to_branch_id).eq('item_id', question.item_id).maybeSingle(),
    ]);
    if (liError || stockError) return { failure: CORRECTION_WORDS.failed };
    avgCost = averageAfterCountChange({
      qtyOnHand: stock?.qty_on_hand ?? 0, avg: stock?.avg_unit_cost ?? null,
      delta: Number(question.extra_qty) || 0, unitCost: li?.unit_cost_at_transfer ?? null,
    });
  }
  const { data, error } = await supabase.rpc('answer_load_extra', {
    p_extra_id: question.id, p_answer: answer, p_avg_cost: avgCost,
  });
  if (error) return answered(error);
  if (!data || data.extra_id == null || data.status !== ANSWER_STATUS[answer]) return { failure: CORRECTION_WORDS.failed };
  if (answer === 'went' && (data.correction_id == null || data.booked_tx_id == null)) {
    return { failure: CORRECTION_WORDS.failed };
  }
  await notifyExtraAnswered({ question, answer, answeredBy: data.answered_by ?? null, branchNames, itemName, transferNumber });
  return { result: data };
}

// ── THE CARD'S WORDS (F2, "Question card"; P6, 682 D7, D16) ──────────────────
//
// Numbers print as a person writes them; a sentence whose subject is a number
// fits one and many alike (#705).
const isAre = n => (Number(n) === 1 ? 'is' : 'are');

export function truckQuestionHead({ loadRef, from, dest }) {
  return `${loadRef} · ${from} → ${dest}`;
}

export function truckQuestionText({ dest, extra, item, loadRef, counted }) {
  return `${dest} counted ${qtyText(extra)} more ${item} on ${loadRef} than you recorded. `
    + `Did ${qtyText(counted)} go on the truck?`;
}

// 682 D16, in answer_load_extra()'s own words: "all {asked} are" when the
// trucks carry everything asked for, otherwise how many they carry.
export function truckQuestionNoRoom({ dest, extra, item, loadRef, transferNumber, asked, shipped }) {
  const onTrucks = Number(shipped) >= Number(asked)
    ? (Number(asked) === 1 ? 'it is on a truck' : `all ${qtyText(asked)} are on trucks`)
    : `${qtyText(shipped)} ${isAre(shipped)} on ${Number(shipped) === 1 ? 'a truck' : 'trucks'}`;
  return `${dest} counted ${qtyText(extra)} more ${item} on ${loadRef} than you recorded. `
    + `${transferNumber || 'This transfer'} asked for ${qtyText(asked)} and ${onTrucks}, so the extra can't go on it.`;
}

export const yesLabel = counted => `Yes, ${qtyText(counted)} went`;
export const noLabel = loaded => `No, ${qtyText(loaded)} went`;
export const SETTLE_LABEL = 'Settle with a stock count';

// ── AWAITING CONFIRMATION'S QUESTIONS (682 D13; F1, F4) ──────────────────────
//
// Every question still asked of one branch, with what each card needs: its
// transfer's lines (the order's room, 682 D16), that transfer's trucks (the
// other trucks' loads and the truck's loader), and the truck corrections that
// decide who recorded the line's current number. Read on every tier, never
// behind a width guard (F4). A failed read draws no cards rather than wrong
// ones; the tab's own transfers are unaffected.
export async function fetchTruckQuestions(branchId) {
  if (branchId == null) return { data: [], error: null };
  const { data: questions, error } = await supabase
    .from('transfer_load_extras').select('*')
    .eq('from_branch_id', branchId).eq('status', 'asked')
    .order('id', { ascending: true });
  if (error) return { data: [], error };
  if (!questions || questions.length === 0) return { data: [], error: null };
  const transferIds = [...new Set(questions.map(q => q.transfer_id))];
  const lineIds = [...new Set(questions.map(q => q.load_item_id))];
  const [t, l, c] = await Promise.all([
    supabase.from('branch_transfers')
      .select('id, status, transfer_number, from_branch_id, to_branch_id, fulfilment_mode, branch_transfer_items(item_id, qty_requested, qty_ready, line_status)')
      .in('id', transferIds),
    supabase.from('branch_transfer_loads')
      .select('id, transfer_id, status, from_branch_id, loaded_by, load_ref, branch_transfer_load_items(id, item_id, qty_loaded, inventory_items(id, name))')
      .in('transfer_id', transferIds),
    supabase.from('stock_corrections').select('id, kind, load_item_id, corrected_by')
      .eq('kind', 'load').in('load_item_id', lineIds),
  ]);
  const failed = t.error || l.error || c.error;
  if (failed) return { data: [], error: failed };
  const transfers = new Map((t.data || []).map(x => [String(x.id), x]));
  const loads = l.data || [];
  return {
    data: questions.map(q => {
      const own = loads.filter(x => String(x.transfer_id) === String(q.transfer_id));
      const truck = own.find(x => String(x.id) === String(q.load_id)) || null;
      const line = (truck?.branch_transfer_load_items || []).find(li => String(li.id) === String(q.load_item_id));
      return {
        question: q,
        transfer: transfers.get(String(q.transfer_id)) || null,
        loads: own,
        recorder: truckLineRecorder({ corrections: c.data || [], loadItemId: q.load_item_id, loadedBy: truck?.loaded_by ?? null }),
        itemName: line?.inventory_items?.name || null,
      };
    }),
    error: null,
  };
}

// ONE CARD'S WORDS, from one read row: its head, its question or 682 D16's
// no-room sentence, and its buttons' labels.
export function truckQuestionEntry(row, { branchNames = {} } = {}) {
  const q = row?.question;
  if (!q) return null;
  const dest = branchNames[String(q.to_branch_id)] || 'The branch that counted it';
  const item = row.itemName || 'of these';
  const room = extraRoom({ question: q, transfer: row.transfer, loads: row.loads });
  return {
    key: String(q.id),
    head: truckQuestionHead({ loadRef: q.load_ref, from: branchNames[String(q.from_branch_id)] || 'The sending branch', dest }),
    text: room.room
      ? truckQuestionText({ dest, extra: q.extra_qty, item, loadRef: q.load_ref, counted: q.counted_qty })
      : truckQuestionNoRoom({
        dest, extra: q.extra_qty, item, loadRef: q.load_ref,
        transferNumber: row.transfer?.transfer_number || q.transfer_number, asked: room.asked, shipped: room.shipped,
      }),
    room: room.room,
    yesLabel: yesLabel(q.counted_qty),
    noLabel: noLabel(q.loaded_qty),
    settleLabel: SETTLE_LABEL,
  };
}

// S6 AND CALLOUT U: THE COUNTING BRANCH, OPENING THE TRANSFER FROM HOUSTON'S YES
// BELL -- "Houston confirmed 4 went — the other 2 Stake Push 24 are now on
// Dallas's shelf." The latest confirmed question on the transfer; null if none.
export function extraConfirmedAlert({ extras = [], branchNames = {}, items = {} } = {}) {
  const yes = (Array.isArray(extras) ? extras : [])
    .filter(x => x && x.status === 'confirmed')
    .sort((a, b) => String(b.answered_at || '').localeCompare(String(a.answered_at || '')))[0];
  if (!yes) return null;
  return extraYesSentence({
    from: branchNames[String(yes.from_branch_id)] || 'The sending branch',
    dest: branchNames[String(yes.to_branch_id)] || 'the counting branch',
    item: items[String(yes.item_id)]?.name || 'of these',
    counted: yes.counted_qty, extra: yes.extra_qty,
  });
}

// THE THREE ANSWERS AS THE COUNTING BRANCH READS THEM -- S6 after Yes, S5 after
// No, the Settle line (F2) -- one place, because the history's Alert, S6's box
// and the answer's bell (transfer_extra_answered, B5) all say them.
export function extraYesSentence({ from, dest, item, counted, extra }) {
  return `${from} confirmed ${qtyText(counted)} went — the other ${qtyText(extra)} ${item} `
    + `${isAre(extra)} now on ${dest}'s shelf.`;
}
export function extraNoSentence({ from, loaded, extra }) {
  const one = Number(extra) === 1;
  return `${from} says ${qtyText(loaded)} went on the truck. The other ${qtyText(extra)} `
    + `${one ? "isn't" : "aren't"} on either shelf until a stock count settles ${one ? 'it' : 'them'}.`;
}
export function extraSettleSentence({ from, extra, transferNumber }) {
  const one = Number(extra) === 1;
  return `${from} says the extra ${qtyText(extra)} can't go on ${transferNumber || 'this transfer'}. `
    + `A stock count at each branch settles ${one ? 'it' : 'them'}.`;
}

// THE ANSWER, kept on the card in its place until the next press (F2,
// "Question card · answered"; gate 665's pinned card).
export function truckAnswerText({ answer, loadRef, counted, loaded, extra, dest }) {
  if (answer === 'went') {
    return `Corrected ${loadRef} to ${qtyText(counted)}. The other ${qtyText(extra)} ${isAre(extra)} on ${dest}'s shelf.`;
  }
  if (answer === 'did_not_go') return `${dest} has been told ${qtyText(loaded)} went.`;
  if (answer === 'settle') return `${dest} has been told. Count both shelves to settle the ${qtyText(extra)}.`;
  return null;
}

// ── #761, T5, Session 79.22b.6c.3 -- CORRECT ON THE ITEM'S MADE LINE ─────────
//
// THE DATABASE'S OWN TEST, COPIED CLAUSE BY CLAUSE, so the item page never
// offers a Correct that correct_record_production() refuses (statement 13 of
// 20261005_correct_sign_off_and_shelf_run_702_761.sql; 79.22b.6c.2b's lesson,
// when a shorter copy offered Austin's leads a Correct that always failed):
//   1. a run for the shelf carries no transfer;
//   2. a correction's fresh record is a shelf run only if its correction is;
//   3. an original record is a sign-off's when a sign-off run matches it - same
//      item, same person, same moment, same amount, neither taken back nor a
//      correction's - and a sign-off is corrected on its transfer;
//   4. the step's LATEST live record: nothing has taken it back;
//   5. the person: at the run's branch, its current recorder or a branch lead,
//      who may sign off (assert_caller_at + assert_may_correct = canCorrectStep).
// Anything unknown - a read that failed - answers no: no button, never a
// button the database might refuse.

// A moment as the database compares it, to the microsecond, whatever format
// each table's reply used ("…26.714549+00:00" and "…26.714549Z" are one moment).
export function instantKey(ts) {
  if (ts == null) return null;
  const s = String(ts).trim();
  const m = /^(\d{4}-\d\d-\d\d)[T ](\d\d:\d\d:\d\d)(?:\.(\d+))?(Z|[+-]\d\d(?::?\d\d)?)?$/.exec(s);
  if (!m) return s;
  let tz = m[4] || 'Z';
  if (tz !== 'Z') tz = tz.length === 3 ? `${tz}:00` : tz.replace(/^([+-]\d\d)(\d\d)$/, '$1:$2');
  const base = Date.parse(`${m[1]}T${m[2]}${tz}`);
  if (!Number.isFinite(base)) return s;
  return `${base}.${(m[3] || '').padEnd(6, '0').slice(0, 6)}`;
}

export function isShelfRunRecord(tx, facts) {
  if (!tx || tx.tx_type !== 'production_in' || !facts) return false;
  // EVERY FACT THE TEST NEEDS MUST BE ON THE RECORD; one missing answers no.
  // Found on screen at 79.22b.6c.3: the item page's read left out item_id, so no
  // sign-off ever matched and three sign-offs showed a Correct the database refuses.
  if (tx.item_id == null || tx.user_id == null || tx.created_at == null || tx.qty == null
    || !('transfer_id' in tx) || !('correction_id' in tx)) return false;
  if (tx.transfer_id != null) return false;                                        // 1
  if (tx.correction_id != null) {                                                  // 2
    return (facts.correctionKinds || {})[String(tx.correction_id)] === 'shelf_run';
  }
  const at = instantKey(tx.created_at);                                            // 3
  return !(facts.signOffRuns || []).some(p => p
    && p.reverses_id == null && p.correction_id == null
    && String(p.item_id) === String(tx.item_id)
    && String(p.signed_off_by) === String(tx.user_id)
    && instantKey(p.signed_off_at) === at
    && Number(p.qty) === Number(tx.qty));
}

export function shelfRunCorrectable(auth, tx, facts) {
  if (!isShelfRunRecord(tx, facts)) return false;
  if (facts.reversed && facts.reversed.has(String(tx.id))) return false;           // 4
  return canCorrectStep(auth, { kind: 'shelf_run', branchId: tx.branch_id, recordedBy: tx.user_id }); // 5
}

// WHAT THE TEST NEEDS, for the Made lines the item page shows - every read
// bounded by those few records. A failed read returns null: no Correct at all.
export async function fetchShelfRunFacts(transactions) {
  const runs = (Array.isArray(transactions) ? transactions : [])
    .filter(t => t && t.tx_type === 'production_in' && t.id != null);
  const empty = { correctionKinds: {}, signOffRuns: [], reversed: new Set(), names: {} };
  if (!runs.length) return { data: empty, error: null };
  const ids = runs.map(t => t.id);
  const corrIds = [...new Set(runs.map(t => t.correction_id).filter(v => v != null))];
  const times = [...new Set(runs.filter(t => t.correction_id == null && t.transfer_id == null)
    .map(t => t.created_at).filter(Boolean))];
  const people = [...new Set(runs.map(t => t.user_id).filter(Boolean).map(String))];
  const none = Promise.resolve({ data: [], error: null });
  const [rev, corr, signed, names] = await Promise.all([
    supabase.from('inventory_transactions').select('reverses_tx_id').in('reverses_tx_id', ids),
    corrIds.length ? supabase.from('stock_corrections').select('id, kind').in('id', corrIds) : none,
    times.length
      ? supabase.from('branch_transfer_production')
        .select('signed_off_by, signed_off_at, qty, reverses_id, correction_id, branch_transfer_items(item_id)')
        .in('signed_off_at', times)
      : none,
    people.length ? supabase.from('users').select('id, full_name').in('id', people) : none,
  ]);
  const failed = rev.error || corr.error || signed.error;
  if (failed) return { data: null, error: failed };
  return {
    data: {
      reversed: new Set((rev.data || []).map(r => String(r.reverses_tx_id))),
      correctionKinds: Object.fromEntries((corr.data || []).map(c => [String(c.id), c.kind])),
      signOffRuns: (signed.data || []).map(p => ({ ...p, item_id: p.branch_transfer_items?.item_id ?? null })),
      names: Object.fromEntries((names.data || []).map(u => [String(u.id), u.full_name])),
    },
    error: null,
  };
}

// ── THE FIVE BELLS (gate B5, 682 D9), Session 79.22b.6c.3 ─────────────────────
//
// In the app only, never email. Nobody is told about their own press (#500).
// The immediate Undo sends nothing (#694). Sent AFTER the step has landed and
// best-effort: a bell that fails never hides a correction the database proved.
// Every event type below is written as a literal at its sendNotification() call,
// so transferNotificationSwitches.test.js can hold each one to its registries.

const STEP_NOUN = Object.freeze({ sign_off: 'sign-off', load: 'truck', count: 'count' });

// #646 -- one full stop at the end of a bell, even when the reason's own note
// already ends a sentence.
function endOnce(s) {
  return /[.!?][”"]?$/.test(s) ? s : `${s}.`;
}

export function correctionBellWords({
  kind, transferNumber = null, loadRef = null, item, was, now, made = null, asked = null, branch, by, reason,
}) {
  const w = qtyText(was);
  const n = qtyText(now);
  const why = reason || 'Corrected';
  if (kind === 'shelf_run') {
    return {
      other: null,
      owner: {
        title: 'Your run for the shelf was corrected',
        message: endOnce(`${by} corrected your run of ${item} for ${branch}'s shelf: ${n}, not ${w} · ${why}`),
      },
    };
  }
  const noun = STEP_NOUN[kind];
  const other = kind === 'sign_off'
    ? `${branch} corrected a sign-off on ${transferNumber}: ${n} ${item} made, not ${w} · ${qtyText(made)} of ${qtyText(asked)} made.`
    : kind === 'load'
      ? `${branch} corrected ${loadRef}: the truck carried ${n} ${item}, not ${w}.`
      : `${branch} corrected its count of ${loadRef}: ${n} ${item} counted in, not ${w}.`;
  return {
    other: { title: `${transferNumber} corrected`, message: other },
    owner: {
      title: `Your ${noun} was corrected`,
      message: endOnce(`${by} corrected your ${noun} on ${transferNumber}: ${n} ${item}, not ${w} · ${why}`),
    },
  };
}

// AFTER A CORRECTION SAVES (CorrectionSheet.js, the one save path for all four
// kinds). The correction's own record is read back for who and what, so the
// words are the database's facts, not the screen's. `transfer` decides "the
// other branch": the asking branch's approvers for a sign-off or a truck, the
// sending branch's for a count - never for an own-shelf request, whose asker is
// the branch doing the work (553 D26's reasoning, as the sign-off bell has it).
export async function notifyCorrection({
  kind, result, transfer = null, itemName = null, asked = null, branchNames = {}, itemNames = {},
} = {}) {
  if (!result) return;
  try {
    if (result.correction_id != null) {
      const { data: c } = await supabase.from('stock_corrections')
        .select('kind, branch_id, item_id, transfer_id, transfer_number, load_ref, was_qty, now_qty, reason, note, '
          + 'step_recorded_by, corrected_by, corrected_by_name')
        .eq('id', result.correction_id).maybeSingle();
      if (c) {
        const words = correctionBellWords({
          kind: c.kind, transferNumber: c.transfer_number, loadRef: c.load_ref,
          item: itemName || 'of these', was: c.was_qty, now: c.now_qty,
          made: result.made, asked,
          branch: branchNames[String(c.branch_id)] || 'The branch', by: c.corrected_by_name || 'Someone',
          reason: reasonText(c.reason, c.note),
        });
        const mine = id => id && String(id) !== String(c.corrected_by);
        if (words.other && transfer && !isOwnShelf(transfer)) {
          const approvers = c.kind === 'count'
            ? await getRequestApproverIds(transfer.from_branch_id, null)
            : await askingApproverIds(transfer);
          const audience = [...new Set((approvers || []).filter(mine))];
          if (audience.length) {
            await sendNotification(audience, words.other.title, words.other.message,
              'transfer_corrected', 'inventory', 'transfers', c.transfer_id);
          }
        }
        if (mine(c.step_recorded_by)) {
          if (c.kind === 'shelf_run') {
            await sendNotification([c.step_recorded_by], words.owner.title, words.owner.message,
              'production_run_corrected', 'inventory', 'inv_detail', c.item_id);
          } else {
            await sendNotification([c.step_recorded_by], words.owner.title, words.owner.message,
              'transfer_step_corrected', 'inventory', 'transfers', c.transfer_id);
          }
        }
      }
    }
    // A count corrected above its truck asks the sender, as Count in does.
    if (kind === 'count' && Array.isArray(result.asked) && result.asked.length) {
      await notifyTruckQuestions({ asked: result.asked, branchNames, itemNames });
    }
  } catch (e) {
    console.error('[#682] correction bell failed', e);
  }
}

// The person row a predicate reads, built from a users row.
function authOf(u) {
  return {
    isAdmin: u.role === 'admin', isExecutive: u.role === 'executive', isManager: u.role === 'manager',
    isTeamMember: u.role === 'team_member', isPurchasing: u.role === 'purchasing',
    profile: { id: u.id, role: u.role, branch_id: u.branch_id, manager_scope: u.manager_scope },
  };
}

// "DALLAS COUNTED MORE THAN YOUR LOAD SHEET" -- to everyone who can answer
// (682 D3): the truck line's current recorder and the sending branch's branch
// managers, admins and executives whose home branch it is. The candidates are
// fetched and canAnswerTruckExtra() decides, never a second copy of the rule in
// a query (getInvoiceReconcilerIds()'s pattern). Nobody: #650's rule asks every
// admin and executive, because the question would otherwise wait on no one.
export async function notifyTruckQuestions({ asked = [], branchNames = {}, itemNames = {} } = {}) {
  const ids = [...new Set((Array.isArray(asked) ? asked : []).map(a => a?.extra_id).filter(v => v != null))];
  if (!ids.length) return;
  try {
    const { data: questions } = await supabase.from('transfer_load_extras').select('*').in('id', ids);
    for (const q of questions || []) {
      if (q.status !== 'asked') continue;
      const [{ data: load }, { data: fixes }, { data: item }] = await Promise.all([
        supabase.from('branch_transfer_loads').select('loaded_by').eq('id', q.load_id).maybeSingle(),
        supabase.from('stock_corrections').select('id, kind, load_item_id, corrected_by')
          .eq('kind', 'load').eq('load_item_id', q.load_item_id),
        itemNames[String(q.item_id)]
          ? Promise.resolve({ data: { name: itemNames[String(q.item_id)] } })
          : supabase.from('inventory_items').select('name').eq('id', q.item_id).maybeSingle(),
      ]);
      const recorder = truckLineRecorder({ corrections: fixes || [], loadItemId: q.load_item_id, loadedBy: load?.loaded_by ?? null });
      const [{ data: leads }, { data: rec }] = await Promise.all([
        supabase.from('users').select('id, role, branch_id, manager_scope')
          .eq('is_active', true).eq('branch_id', q.from_branch_id).in('role', ['admin', 'executive', 'manager']),
        recorder
          ? supabase.from('users').select('id, role, branch_id, manager_scope').eq('is_active', true).eq('id', recorder)
          : Promise.resolve({ data: [] }),
      ]);
      const extra = { fromBranchId: q.from_branch_id, loadedBy: recorder };
      const can = [...(leads || []), ...(rec || [])]
        .filter(u => canAnswerTruckExtra(authOf(u), extra))
        .map(u => u.id)
        .filter(id => String(id) !== String(q.counted_by));
      const { ids: audience } = await orEveryoneWhoCanDecide(can, q.from_branch_id);
      const dest = branchNames[String(q.to_branch_id)] || 'The branch that counted it';
      if (audience.length) {
        await sendNotification(
          audience,
          `${dest} counted more than your load sheet`,
          truckQuestionText({ dest, extra: q.extra_qty, item: item?.name || 'of these', loadRef: q.load_ref, counted: q.counted_qty }),
          'transfer_extra_counted', 'inventory', 'transfers', q.transfer_id,
        );
      }
    }
  } catch (e) {
    console.error('[#682] question bell failed', e);
  }
}

// "HOUSTON ANSWERED ABOUT HOU-TRF-00026-3" -- to whoever counted (B5), with S6's
// sentence after Yes, S5's after No, the Settle line after Settle. It opens the
// transfer with S6's box (notificationNav.js). Never the person who answered.
export async function notifyExtraAnswered({ question, answer, answeredBy = null, branchNames = {}, itemName = null, transferNumber = null } = {}) {
  const q = question;
  if (!q?.counted_by || (answeredBy && String(answeredBy) === String(q.counted_by))) return;
  const from = branchNames[String(q.from_branch_id)] || 'The sending branch';
  const dest = branchNames[String(q.to_branch_id)] || 'the counting branch';
  const message = answer === 'went'
    ? extraYesSentence({ from, dest, item: itemName || 'of these', counted: q.counted_qty, extra: q.extra_qty })
    : answer === 'did_not_go'
      ? extraNoSentence({ from, loaded: q.loaded_qty, extra: q.extra_qty })
      : extraSettleSentence({ from, extra: q.extra_qty, transferNumber: transferNumber || q.transfer_number });
  try {
    await sendNotification([q.counted_by], `${from} answered about ${q.load_ref}`, message,
      'transfer_extra_answered', 'inventory', 'transfers', q.transfer_id);
  } catch (e) {
    console.error('[#682] answer bell failed', e);
  }
}
